<template>
  <div
    v-if="show"
    class="contest-notification p-md-3 p-sm-1"
    :class="{ 'has-link': hasLink }"
    @click="redirect()"
  >
    ⚡ {{ notification }}
    <div class="close" @click.stop.prevent="close">
      <span v-if="banners.length >= 1" class="arrow"> ⇢ </span>
      <font-awesome-icon v-else :icon="faTimes" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { emitter } from '@/core/emitter';

const props = defineProps({
  notification: {
    type: String,
    required: true,
  },
  bannerId: {
    type: String,
    required: true,
  },
  action: {
    type: String,
    default: null,
  },
  url: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const userStore = useUserStore();
const show = ref(true);
const banners = [];

const redirectToDeposit = computed({
  get() {
    return userStore.redirectToDeposit;
  },
  set(value) {
    userStore.setRedirectToDeposit(value);
  },
});

if (props.notification.includes('Click here to settle your balance')) {
  redirectToDeposit.value = true;
}

const emit = defineEmits(['closeBanner', 'bannerAction']);

// Underline on hover only when clicking actually navigates somewhere.
const hasLink = computed(
  () => !!props.url || !!props.action || userStore.redirectToDeposit,
);

function redirect() {
  if (props.url) {
    if (/^https?:\/\//i.test(props.url)) {
      window.open(props.url, '_blank', 'noopener');
    } else if (/^\/(?!\/)/.test(props.url)) {
      router.push(props.url);
    }
    return;
  }
  if (props.action) {
    emit('bannerAction', props.action);
    return;
  }
  if (userStore.redirectToDeposit) {
    emitter.emit('showDepositModal');
  }
}

function close() {
  emit('closeBanner', props.bannerId);
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.contest-notification {
  position: relative;
  background: main.$grey-500;
  color: main.$blue-1100;
  text-align: center;
  font-weight: main.$font-weight-semibold;
  font-size: main.$font-size-sm;
  text-shadow: 1px 2px 5px 0 main.$black-201;
  transition: background 0.2s ease;

  &:hover {
    background: main.$blue-1100;
    color: main.$grey-500;
    cursor: pointer;
  }

  &.has-link:hover,
  &.has-link:active {
    text-decoration: underline;
  }
  @media screen and (max-width: map.get(main.$grid-breakpoints, md)) {
    display: flex;
    justify-content: space-around;
    align-items: center;
  }
}
.close {
  position: absolute;
  right: 3rem;
  top: 0;
  height: 100%;
  padding: 0 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 0;
  z-index: 3;
  transition: transform ease 0.2s;
  &:hover {
    transform: scale(1.3);
  }
  @media screen and (max-width: map.get(main.$grid-breakpoints, md)) {
    position: static;
    display: block;
    padding: 1rem;
    height: auto;
  }
}
.arrow {
  font-size: 1.5rem;
  font-weight: main.$font-weight-bold;
}
</style>
